export type TimePeriod = "am" | "pm";

/** A manually typed 12-hour time plus its AM/PM period. */
export interface TimeEntry {
  text: string;
  period: TimePeriod;
}

const pad = (value: number) => value.toString().padStart(2, "0");

/** "14:05" (24-hour) → { text: "02:05", period: "pm" } */
export const timeEntryFrom24h = (time: string): TimeEntry => {
  const [hours, minutes] = time.split(":").map(Number);
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;
  return { text: `${pad(hour12)}:${pad(minutes)}`, period: hours >= 12 ? "pm" : "am" };
};

export const currentTimeEntry = (): TimeEntry => {
  const now = new Date();
  return timeEntryFrom24h(`${pad(now.getHours())}:${pad(now.getMinutes())}`);
};

/** Parses "10:30", "9:05", "1030", "930" or "10" (1–12 hour clock). */
const parseTimeText = (text: string): { hour: number; minute: number } | null => {
  const value = text.trim();
  const match =
    value.match(/^(\d{1,2}):(\d{2})$/) ??
    value.match(/^(\d{1,2})(\d{2})$/) ??
    value.match(/^(\d{1,2})()$/);
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = match[2] ? Number(match[2]) : 0;
  if (hour < 1 || hour > 12 || minute > 59) return null;
  return { hour, minute };
};

/** TimeEntry → 24-hour "HH:mm", or null when the typed text isn't a valid time. */
export const timeEntryTo24h = ({ text, period }: TimeEntry): string | null => {
  const parsed = parseTimeText(text);
  if (!parsed) return null;
  const hours = (parsed.hour % 12) + (period === "pm" ? 12 : 0);
  return `${pad(hours)}:${pad(parsed.minute)}`;
};

/** Normalises valid input to "hh:mm" (e.g. "930" → "09:30"); leaves invalid text as is. */
export const formatTimeEntryText = (text: string): string => {
  const parsed = parseTimeText(text);
  return parsed ? `${pad(parsed.hour)}:${pad(parsed.minute)}` : text;
};

/** Local Date for `day` at the entry's time, or null if the time is invalid. */
export const timeEntryToDate = (
  year: number,
  month: number,
  day: number,
  entry: TimeEntry,
): Date | null => {
  const time = timeEntryTo24h(entry);
  if (!time) return null;
  const [hours, minutes] = time.split(":").map(Number);
  const date = new Date(year, month, day);
  date.setHours(hours, minutes, 0, 0);
  return date;
};
